import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// import loginBanner from '../../assets/images/login/login-banner.png';
// import sample from '../../assets/videos/background.mp4';

import { ReactComponent as TimerIcon } from '../../../assets/icons/clock-icon.svg';
import { appCurrentBranding } from '../../../config/branding';
import { branding } from '../../../config/enum';
import { useAppDispatch } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomPhoneInput from '../../../shared-components/custom-input/CustomPhoneInput';
import CustomOtpInput from '../../../shared-components/custom-otp-input/CustomOtpInput';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import RadioButton from '../../../shared-components/radio-button/RadioButton';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import { emailValidation, phoneNumberValidation } from '../../../shared-functions/Validation';
import authApi from '../../../utils/api/auth';

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}
/**
 * Login page function component
 */
function ConsumerDelete() {
  const { t, i18n } = useTranslation('loginV2');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });
  const [credentials, setCredentials] = useState<{
    emailOrPhoneNumber: string;
    type: string | number;
    countryCode: string;
  }>({
    emailOrPhoneNumber: '',
    type: 'email',
    countryCode: appCurrentBranding === branding.VOLTIE ? '1' : '966',
  });
  const [OTP, setOTP] = useState<string[]>(Array(6).fill(''));
  const [resendTimer, setResendTimer] = useState<number>(30);
  const [otpVerificationOpen, setOtpVerificationOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const intervalReference = useRef<NodeJS.Timeout | null>(null);

  /**
   * Input field change function
   * @param name
   * @param changeValue
   */
  const handleCredentialChange = (name: string, changeValue: string | number) => {
    setCredentials({
      ...credentials,
      [name]: changeValue,
    });
  };

  /**
   * Function to handle Resend OTP timer
   */
  const startResendTimer = () => {
    // Clear any previous interval before starting a new one
    if (intervalReference.current) clearInterval(intervalReference.current);

    setResendTimer(30); // Reset to 30 seconds
    intervalReference.current = setInterval(() => {
      setResendTimer((previous) => {
        if (previous <= 1) {
          if (intervalReference.current) clearInterval(intervalReference.current); // Clear interval at the end
          return 0;
        }
        return previous - 1;
      });
    }, 1000);
  };
  /**
   * Function to resend OTP
   */
  const handleResendOTP = () => {
    if (resendTimer === 0) {
      startResendTimer();
      authApi
        .consumerDelete({
          countryCode: credentials.countryCode,
          emailOrPhoneNumber: credentials.emailOrPhoneNumber,
        })
        .then(() => {})
        .catch((error) => {
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * handle submit function
   */
  const submitFunction = () => {
    setSubmitOpen(true);
    if (
      credentials.countryCode !== '' &&
      (phoneNumberValidation(credentials.emailOrPhoneNumber) ||
        emailValidation(credentials.emailOrPhoneNumber))
    ) {
      setDisabled(true);
      const request = {
        emailOrPhoneNumber: credentials.emailOrPhoneNumber,
        countryCode: credentials.countryCode,
      };
      authApi
        .consumerDelete(request)
        .then(() => {
          setDisabled(false);
          setOtpVerificationOpen(true);
          startResendTimer();
        })
        .catch((error) => {
          setDisabled(false);
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  /**
   * handle submit function
   */
  const submitOTPFunction = () => {
    setSubmitOpen(true);
    if (
      credentials.countryCode !== '' &&
      (phoneNumberValidation(credentials.emailOrPhoneNumber) ||
        emailValidation(credentials.emailOrPhoneNumber))
    ) {
      setDisabled(true);
      const request: any = {
        otp: OTP.join(''),
      };
      if (credentials.type === 'email') {
        request.email = credentials.emailOrPhoneNumber;
      } else {
        request.phoneNumber = credentials.emailOrPhoneNumber;
        request.countryCode = credentials.countryCode;
      }
      authApi
        .consumerDeleteVerificationOTP(request)
        .then(() => {
          setModalBoxStatus('open');
          setModalBoxDetail({
            title: '',
            rightCorner: '',
            content: (
              <SuccessNotification
                title="Consumer Delete Successfully"
                description=""
                notificationCloseTimer={3000}
                onNotificationClose={() => {}}
              />
            ),
            notificationModal: true,
          });
          setTimeout(() => {
            navigate('../login/credentials');
          }, 3000);
        })
        .catch((error) => {
          dispatch(
            setNotificationMessage({
              message: error?.response?.data?.message,
              status: true,
              type: 'error',
              code: error?.response?.data?.code,
            }),
          );
        });
    }
  };

  return (
    <div className="forgot-password-v2-wrap" dir={i18n.dir()}>
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="forgot-password-v2-container">
        <div className="email-phone-toggle-wrap">
          <RadioButton
            name="type"
            value={credentials.type}
            list={[
              {
                label: `${t('email')}`,
                value: 'email',
              },
              {
                label: `${t('phoneNumber')}`,
                value: 'phone',
              },
            ]}
            onChangeValue={handleCredentialChange}
          />
          {credentials.type === 'email' ? (
            <CustomInput
              type="email"
              placeholder={t('enterEmail')}
              name="emailOrPhoneNumber"
              value={credentials.emailOrPhoneNumber}
              onChangeValue={handleCredentialChange}
              error={submitOpen && !emailValidation(credentials.emailOrPhoneNumber)}
              errorMessage={t('emailInvalid')}
            />
          ) : (
            <CustomPhoneInput
              type="number"
              placeholder={t('enterPhoneNumber')}
              name="emailOrPhoneNumber"
              value={credentials.emailOrPhoneNumber}
              value1={credentials.countryCode}
              onChangeValue={handleCredentialChange}
              error={submitOpen && !phoneNumberValidation(credentials.emailOrPhoneNumber)}
              errorMessage={t('phoneInvalid')}
            />
          )}
          {otpVerificationOpen ? (
            <div className="otp-v2-delete-consumer">
              <CustomOtpInput
                length={6}
                OTP={OTP}
                setOTP={setOTP}
                type="number"
                inputLabel={t('enterOtp')}
                error={submitOpen && OTP.some((digit) => digit === '')}
              />
              <div className="resend-otp-v2-wrap">
                <div className="resend-timer-v2-wrap">
                  <span className="resend-icon">
                    <TimerIcon />
                  </span>
                  <span>00:{resendTimer < 10 ? `0${resendTimer}` : resendTimer}</span>
                </div>
                <p
                  role="presentation"
                  onClick={handleResendOTP}
                  className={`resendOtp ${resendTimer > 0 ? 'disabled' : ''}`}>
                  {t('resend')}
                </p>
              </div>
              <div className="submit-button-container">
                <MyButton
                  label={t('proceed')}
                  buttonType="submit"
                  onClickFunc={submitOTPFunction}
                  disabled={OTP.length !== 6 || OTP.some((digit) => digit === '')}
                />
              </div>
            </div>
          ) : (
            <MyButton
              label={t('getOtp')}
              buttonType="submit"
              onClickFunc={submitFunction}
              disabled={disabled}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ConsumerDelete;
